import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { updateWebinar } from '@/features/webinar/webinarThunks';
import type { Webinar } from '@/features/webinar/types';
import type { AppError } from '@/features/auth/types';
import { mapHttpError } from '@/lib/mapApiError';
import { isYoutubeUrl } from '@/lib/youtube';

const createDiffusionFormSchema = (t: (key: string) => string) =>
  z
    .object({
      transmissionMode: z.enum(['NATIVE', 'YOUTUBE']),
      youtubeUrl: z.string().max(200).optional().or(z.literal('')),
    })
    .superRefine((data, ctx) => {
      // L'URL peut rester vide (brouillon) : on ne valide le format que si elle est renseignée.
      const hasUrl = !!data.youtubeUrl && data.youtubeUrl.trim() !== '';
      if (data.transmissionMode === 'YOUTUBE' && hasUrl && !isYoutubeUrl(data.youtubeUrl)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['youtubeUrl'],
          message: t('generalForm.validation.youtubeUrlInvalid'),
        });
      }
    });

type DiffusionFormValues = {
  transmissionMode: 'NATIVE' | 'YOUTUBE';
  youtubeUrl?: string;
};

interface DiffusionFormProps {
  webinar?: Webinar | null;
  onSuccess?: () => void;
}

export default function DiffusionForm({ webinar, onSuccess }: Readonly<DiffusionFormProps>) {
  const { t } = useTranslation('createWebinar');
  const { t: tCommon } = useTranslation('common');
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<DiffusionFormValues>({
    resolver: zodResolver(createDiffusionFormSchema(t)),
    defaultValues: {
      transmissionMode: webinar?.transmissionMode ?? 'NATIVE',
      youtubeUrl: webinar?.youtubeUrl ?? '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    reset({
      transmissionMode: webinar?.transmissionMode ?? 'NATIVE',
      youtubeUrl: webinar?.youtubeUrl ?? '',
    });
  }, [webinar?.id, webinar?.transmissionMode, webinar?.youtubeUrl, reset]);

  const selectedTransmissionMode = watch('transmissionMode');

  const onSubmit = async (data: DiffusionFormValues) => {
    if (!workspaceId || !webinar?.id) {
      return;
    }

    const result = await dispatch(
      updateWebinar({
        workspaceId,
        id: webinar.id,
        input: {
          // L'update back écrit titre/description sans condition : on les renvoie inchangés pour ne pas les effacer.
          title: webinar.title,
          shortDescription: webinar.shortDescription ?? '',
          description: webinar.description ?? '',
          transmissionMode: data.transmissionMode,
          youtubeUrl: data.transmissionMode === 'YOUTUBE' ? data.youtubeUrl?.trim() : undefined,
        },
      }),
    );

    if (updateWebinar.fulfilled.match(result)) {
      toast.success(t('generalForm.saveSuccess.update'));
      onSuccess?.();
    } else if (updateWebinar.rejected.match(result)) {
      const error = result.payload as AppError | undefined;
      const message = error?.message
        ? tCommon(mapHttpError(error.message), error.message)
        : tCommon('errors.generic', 'Une erreur est survenue lors de la sauvegarde.');
      toast.error(message);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label>{t('generalForm.transmission.label')}</Label>
        <Controller
          control={control}
          name="transmissionMode"
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              className="flex flex-col gap-2"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="NATIVE" id="diffusion-native" />
                <Label htmlFor="diffusion-native">{t('generalForm.transmission.native')}</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="YOUTUBE" id="diffusion-youtube" />
                <Label htmlFor="diffusion-youtube">{t('generalForm.transmission.youtube')}</Label>
              </div>
            </RadioGroup>
          )}
        />
        {selectedTransmissionMode === 'YOUTUBE' && (
          <Controller
            control={control}
            name="youtubeUrl"
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  type="url"
                  placeholder="https://www.youtube.com/watch?v=XXXXXXXXXXX"
                  className="w-full px-4 py-2 border text-base bg-white border-input rounded-lg focus:outline-none"
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(event.target.value)}
                  maxLength={200}
                />
                {errors.youtubeUrl && (
                  <p className="text-xs text-error">{errors.youtubeUrl.message}</p>
                )}
              </div>
            )}
          />
        )}
        {selectedTransmissionMode === 'YOUTUBE' && (
          <p className="text-xs text-secondary">
            {t('generalForm.transmission.mp4ReplayUnavailable')}
          </p>
        )}
      </div>
      <div className="w-full">
        <Button
          type="submit"
          variant="default"
          disabled={isSubmitting || !isValid}
          className="w-full"
        >
          {isSubmitting && <Loader />}
          {t('generalForm.saveButton')}
        </Button>
      </div>
    </form>
  );
}
